import { useCallback, useEffect, useState, useContext, useRef } from "react";
import { initializeApp } from "firebase/app";
import {
  getDownloadURL,
  getStorage,
  ref,
  getMetadata,
  uploadBytesResumable,
  deleteObject
} from "firebase/storage";
import { getAuth } from "firebase/auth";
import {
  Timestamp,
  arrayUnion,
  arrayRemove,
  deleteField,
  initializeFirestore,
  collection,
  getDocs,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot
} from "firebase/firestore";
import { getPerformance } from "firebase/performance";

import { useAuthState } from "react-firebase-hooks/auth";
import dayjs from "dayjs";
import useSWR, { useSWRConfig } from "swr";
import { isEqual } from "lodash";

import { isCountryUsa } from "@aclymatepackages/other-helpers";

import { isObjectEmpty, generateTempId } from "./otherHelpers";
import { getAccountId, getAccountCollection } from "./accountHelpers";
import { AdminDataContext } from "./contexts/adminDataContext";
import usePlatformLayoutContext from "./contexts/platformLayout";
import useSimulationContext from "./contexts/simulationContext";
import { PermissionsContext } from "./contexts/permissionsContext";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);
export const storage = getStorage(app);
export const db = initializeFirestore(app, { ignoreUndefinedProperties: true });
export const auth = getAuth(app);
export const fetchAuth = () => auth || getAuth(app);

getPerformance(app);

const adminDataCollections = [
  "offices",
  "employees",
  "vehicles",
  "offsets",
  "offset-subscriptions",
  "events",
  "vendors",
  "tasks",
  "audit-logs",
  "vendor-relationships",
  "all-aggregated-transactions",
  "matched-vendors-aggregated-transactions",
  "employees-aggregated-transactions",
  "events-aggregated-transactions"
];

const nonCompanyCollectionNames = {
  events: "events",
  employees: "individuals",
  "vendor-relationships": "vendor-relationships",
  "audit-logs": "audit-logs",
  "product-components": "product-components",
  "product-lifecycle-events": "product-lifecycle-events",
  customers: "vendor-relationships",
  "vendor-assigned-products": "product-components",
  "vendor-assigned-product-lifecycle-events": "product-lifecycle-events"
};

export const useAuth = () => useAuthState(getAuth());

export const signOut = async () => await getAuth().signOut();

const buildDocRef = (collectionName, docId) => {
  const accountsCollection = getAccountCollection();
  const accountId = getAccountId();

  if (!collectionName) {
    return doc(db, `${accountsCollection}/${accountId}`);
  }

  if (collectionName === "transactions") {
    return doc(db, `transactions/${docId}`);
  }

  if (Object.keys(nonCompanyCollectionNames).includes(collectionName)) {
    return doc(db, `${nonCompanyCollectionNames[collectionName]}/${docId}`);
  }

  return doc(
    db,
    `${accountsCollection}/${accountId}/${collectionName}/${docId}`
  );
};

const buildCollectionRef = (collectionName) => {
  if (collectionName === "transactions") {
    return collection(db, "transactions");
  }

  if (Object.keys(nonCompanyCollectionNames).includes(collectionName)) {
    const collectionPath = nonCompanyCollectionNames[collectionName];
    return collection(db, collectionPath);
  }

  const accountsCollection = getAccountCollection();
  const accountId = getAccountId();

  return collection(db, `${accountsCollection}/${accountId}/${collectionName}`);
};

const buildCollectionQuery = (collectionName, queries = []) => {
  const accountId = getAccountId();

  const collectionQueries = {
    employees: [["currentLinkedIds", "array-contains", accountId]],
    events: [["companyIds", "array-contains", accountId]],
    "vendor-relationships": [["buyerId", "==", accountId]],
    transactions: [["buyerId", "==", accountId]],
    "product-components": [["buyerIds", "array-contains", accountId]],
    "product-lifecycle-events": [["ownerId", "==", accountId]],
    customers: [["vendorId", "==", accountId]],
    "vendor-assigned-products": [["vendorIds", "array-contains", accountId]],
    "vendor-assigned-product-lifecycle-events": [["vendor.id", "==", accountId]]
  };

  const archivedQuery =
    collectionName === "vehicles" ||
    collectionName === "transactions" ||
    collectionName === "vendor-relationships" ||
    collectionName === "customers" ||
    collectionName === "product-components" ||
    collectionName === "vendor-assigned-products" ||
    collectionName === "action-items"
      ? [["archived", "==", false]]
      : [];

  const additionalQueries = collectionQueries[collectionName] || [];

  const queriesArray = [...additionalQueries, ...archivedQuery, ...queries].map(
    (queryArray) => where(...queryArray)
  );

  const collectionRef = buildCollectionRef(collectionName);

  return query(collectionRef, ...queriesArray);
};

export const fetchCollectionRefQuery = async (collectionName, queries = []) => {
  const firebaseQuery = buildCollectionQuery(collectionName, queries);

  return await getDocs(firebaseQuery)
    .then((rawData) => {
      return rawData.docs.map((doc) => ({
        ...unpackFirebaseObj(doc.data()),
        id: doc.id
      }));
    })
    .catch((e) => {
      // console.log(`error fetching ${collectionName} documents: ` + e);
      throw new Error(e);
    });
};

const dateToFirebaseTimeStamp = (date = null) => {
  // Validate that the date is not null and represents a valid date
  if (!date || (date instanceof Date && isNaN(date.getTime()))) {
    console.warn("Invalid date passed to dateToFirebaseTimeStamp:", date);
    return null;
  }

  const unixTimestamp = dayjs(date).unix();

  // Check if dayjs conversion resulted in a valid timestamp
  if (isNaN(unixTimestamp)) {
    console.warn("dayjs conversion resulted in NaN for date:", date);
    return null;
  }

  return new Timestamp(unixTimestamp, 0);
};

export const firebaseArrayUnionObj = (data) => ({ data, arrayUnion: true });

const firebaseArrayUnion = (value) => {
  if (Array.isArray(value)) {
    return arrayUnion(...value);
  }
  return arrayUnion(value);
};

export const firebaseArrayRemoveObj = (data) => ({ data, arrayRemove: true });

export const firebaseArrayRemove = (value) => arrayRemove(value);

export const firebaseFieldDeleteObj = () => ({ deletable: true });

export const prepareFirebaseObj = (obj) =>
  Object.fromEntries(
    Object.entries(obj)
      .filter(([, value]) => !(value === null || value === undefined))
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return [
            key,
            value.map((row) =>
              typeof row === "string" || typeof row === "number"
                ? row
                : prepareFirebaseObj(row)
            )
          ];
        }

        if (typeof value === "string" && !isNaN(value)) {
          return [key, Number(value)];
        }

        if (
          typeof key === "string" &&
          typeof value === "string" &&
          RegExp(/^\d{1,2}(\/|-)\d{1,2}(\/|-)(?:\d{4}|\d{2})$/, "g").test(
            value
          ) &&
          !isObjectEmpty(value)
        ) {
          const timestamp = dateToFirebaseTimeStamp(new Date(value));
          return timestamp ? [key, timestamp] : [key, null];
        }

        if (value && typeof value === "object") {
          if (dayjs.isDayjs(value)) {
            const convertedToDateObj = dayjs(value).toDate();
            const timestamp = dateToFirebaseTimeStamp(convertedToDateObj);
            return timestamp ? [key, timestamp] : [key, null];
          }

          if (Object.prototype.toString.call(value) === "[object Date]") {
            // Validate that the Date object represents a valid date
            if (isNaN(value.getTime())) {
              console.warn(
                "Invalid Date object found in prepareFirebaseObj:",
                key,
                value
              );
              return [key, null];
            }
            const timestamp = dateToFirebaseTimeStamp(dayjs(value).toDate());
            return timestamp ? [key, timestamp] : [key, null];
          }

          const { seconds, data, arrayUnion, arrayRemove, deletable } = value;

          if (seconds) {
            return [key, value];
          }
          if (arrayUnion && data) {
            return [key, firebaseArrayUnion(data)];
          }

          if (arrayRemove && data) {
            return [key, firebaseArrayRemove(data)];
          }

          if (deletable) {
            return [key, deleteField()];
          }

          return [key, prepareFirebaseObj(value)];
        }

        return [key, value];
      })
  );

export const unpackFirebaseObj = (obj) =>
  Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      if (Array.isArray(value)) {
        return [
          key,
          value.map((row) =>
            typeof row === "string" || typeof row === "number"
              ? row
              : unpackFirebaseObj(row)
          )
        ];
      }
      if (value && typeof value === "object") {
        const { seconds, _seconds } = value || {};
        if (seconds || _seconds) {
          return [key, new Date((seconds || _seconds) * 1000)];
        }
        return [key, unpackFirebaseObj(value)];
      }
      return [key, value];
    })
  );

export const useDestructuredSWRdata = (queryCache, fetcher) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const { data: rawData, error: rawError } = useSWR(queryCache, fetcher);

  useEffect(() => {
    if (!rawError && (!isObjectEmpty(rawData) || Array.isArray(rawData))) {
      setData(rawData);
      setLoading(false);
    }
    if (rawError) {
      setError(rawError);
      setLoading(false);
    }
  }, [rawData, rawError]);

  return [data, loading, error];
};

export const fetchAccountData = async () =>
  await getDoc(buildDocRef())
    .then((doc) => unpackFirebaseObj({ ...doc.data(), id: doc.id }))
    .catch((error) => {
      // console.log("account data error", error);
      throw new Error(error);
    });

export const useAccountData = () => {
  const { viewMode } = usePlatformLayoutContext() || {};
  const { adminData = {}, isAdminDataLoading } =
    useContext(AdminDataContext) || {};
  const simulationContext = useSimulationContext() || {};

  // Keyed by account id (like useCachedDisplayData's [collection, queriesKey] --
  // see PR #1700) so switching accounts can't have this serve the previous
  // account's cached doc before the new fetch resolves.
  const accountId = getAccountId();

  const accountDataFromSWR = useDestructuredSWRdata(
    ["accountData", accountId],
    () => fetchAccountData(),
    {}
  );

  const insertDefaultCompanyParams = (companyData) => {
    const { startDate, measurementSystem, name, branding, address, isRemote } =
      companyData || {};
    const { companyStatement } = branding || {};

    const defaultCompanyStatement =
      branding?.companyStatement ||
      `${name} is using Aclymate to measure, reduce, report and offset its carbon footprint. The company is doing this because it's committed to being transparent about its carbon footprint to all internal and external stakeholders.`;

    const defaultMeasurementSystem = isCountryUsa(address?.country)
      ? "imperial"
      : "metric";

    return {
      ...companyData,
      isRemote: isRemote || false,
      startDate,
      measurementSystem: measurementSystem || defaultMeasurementSystem,
      branding: {
        ...branding,
        companyStatement: companyStatement || defaultCompanyStatement
      }
    };
  };

  if (viewMode === "simulation") {
    return [simulationContext.accountData || {}, false];
  }

  if (viewMode === "admin") {
    const { companyData } = adminData;

    return [insertDefaultCompanyParams(companyData), isAdminDataLoading];
  }

  const [accountData, ...otherProps] = accountDataFromSWR;

  return [
    insertDefaultCompanyParams(Array.isArray(accountData) ? {} : accountData),
    ...otherProps
  ];
};

// Invalidates the current account's useAccountData cache slot. Use on logout so
// a stale entry can't survive to be served under a matching accountId later
// (e.g. a different user signing into the same account in the same tab).
export const useMutateAccountData = () => {
  const { mutate } = useSWRConfig();

  return useCallback(
    async (accountId) => await mutate(["accountData", accountId], undefined, true),
    [mutate]
  );
};

const mutateNestedData = (existingObj, updateObj) =>
  Object.fromEntries(
    Object.entries(updateObj).map(([key, value]) => {
      if (!key.includes(".")) {
        return [key, value];
      }
      const keys = key.split(".");
      const existingData = existingObj[keys[0]];
      return [keys[0], { ...existingData, [keys[1]]: value }];
    })
  );

// Date objects are serialized with a type tag so they round-trip correctly
// for comparison/cache-key purposes, without converting them to plain
// strings that Firestore can't use in Timestamp queries.
const serializeQueriesKey = (queries) =>
  JSON.stringify(queries, (_key, value) => {
    if (value instanceof Date) {
      return { __type: "Date", value: value.toISOString() };
    }
    return value;
  });

const EMPTY_QUERIES_KEY = serializeQueriesKey([]);

// Every [collection, queriesKey] cache slot useCachedDisplayData has ever read,
// so a write can invalidate every filtered variant of a collection instead of
// only the one it happens to know about. SWR 1.3.0 has no key-matching form of
// mutate (that arrived in 2.0), and the keys must be visible to writers in
// unrelated component trees, so this is deliberately mutable module state --
// the same shape as SWR's own default cache provider. It is grow-only for the
// same reason that cache never evicts: a slot that unmounts keeps its data and
// would serve it again on remount, so it still has to be invalidated.
const cachedCollectionQueryKeys = new Map();

const registerCachedCollectionQueryKey = (collectionName, queriesKey) => {
  const queryKeys = cachedCollectionQueryKeys.get(collectionName) || new Set();
  cachedCollectionQueryKeys.set(collectionName, queryKeys.add(queriesKey));
};

const getCachedCollectionQueryKeys = (collectionName) => [
  ...new Set([
    EMPTY_QUERIES_KEY,
    ...(cachedCollectionQueryKeys.get(collectionName) || [])
  ])
];

// Invalidates every variant of a collection cached by useCachedDisplayData. Use
// this instead of useSWRConfig().mutate(collection) for any collection that
// useCachedDisplayData reads -- a bare-string mutate only reaches the unfiltered
// cache slot and silently misses filtered ones.
//
// updateLocalData is applied to the unfiltered slot alone, and filtered slots
// pick the write up on the refetch below. Patching them directly is not safe at
// either end: an append would leave a row that doesn't satisfy the slot's own
// query (a shipping transaction landing in a read filtered to gas/electricity/
// utilities), and patching a slot whose first fetch is still in flight makes SWR
// discard that fetch and leaves the slot empty until the next revalidation. Both
// produce exactly the input that makes the office gap-fill calc treat a month as
// having no real utility coverage and synthesize modeled emissions for it.
//
// Revalidation passes `undefined` data rather than using mutate's revalidate-only
// arity, so slots with no mounted reader are cleared too and can't serve
// pre-write data on remount. useDestructuredSWRdata ignores undefined payloads,
// so mounted readers keep showing their current rows until the refetch lands.
export const useMutateCachedCollection = () => {
  const { mutate } = useSWRConfig();

  return useCallback(
    async (collectionName, updateLocalData, shouldRevalidate = true) => {
      if (updateLocalData) {
        await mutate(
          [collectionName, EMPTY_QUERIES_KEY],
          updateLocalData,
          false
        );
      }

      if (!shouldRevalidate) {
        return;
      }

      await Promise.all(
        getCachedCollectionQueryKeys(collectionName).map((queriesKey) =>
          mutate([collectionName, queriesKey], undefined, true)
        )
      );
    },
    [mutate]
  );
};

export const useCollectionDataListener = (collection, queries = []) => {
  const { viewMode, isSimulation } = usePlatformLayoutContext() || {};
  const { adminData = {}, isAdminDataLoading } =
    useContext(AdminDataContext) || {};
  const simulationContext = useSimulationContext() || {};

  const [dbData, setDbData] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  const dbDataRef = useRef(dbData);
  const dataLoadingRef = useRef(dataLoading);

  // Track the latest queries in a ref so the effect always uses original values
  // (including Date objects) rather than JSON-parsed strings.
  const queriesRef = useRef(queries);
  queriesRef.current = queries;

  const queriesKey = serializeQueriesKey(queries);

  // Which [collection, queriesKey] the dbData/dataLoading state currently
  // held actually belongs to -- recorded synchronously at the very top of
  // the effect (before any early return, and before onSnapshot's async
  // callback ever fires), not derived from dataLoading itself. This exists
  // to close a real gap the dataLoading reset above (setDataLoading(true)
  // right before resubscribing) can't close on its own: React runs a
  // passive useEffect AFTER a render commits and paints, so there is one
  // fully-committed render -- with the NEW collection prop, but still the
  // OLD dataLoading/dbData state -- before this effect ever runs. A
  // consumer reading that one render (e.g. mergeRollupsV2OfficeDocsInto
  // Offices, groupRollupsV2EmployeeDocsByEmployee) sees loading:false with
  // data for the PREVIOUS subscription, exactly the flash-of-wrong-or-empty
  // -data bug this whole file exists to prevent. Comparing the currently
  // REQUESTED key against the key the stored state actually resolved for
  // lets the render return loading:true on that exact render, synchronously,
  // without waiting for the effect to catch up.
  const resolvedSubscriptionKeyRef = useRef(null);
  const requestedSubscriptionKey = `${collection || ""}::${queriesKey}`;

  useEffect(() => {
    const syncData = () => {
      resolvedSubscriptionKeyRef.current = requestedSubscriptionKey;

      // A falsy collection means "don't listen at all" -- e.g. a rollups-v2
      // read-cutover call site that isn't cut over yet for this company and
      // must not attach a real Firestore listener just to discard whatever
      // it returns (an empty query array still queries a real, possibly
      // populated collection; this skips the query entirely).
      if (!collection) {
        dataLoadingRef.current = false;
        setDataLoading(false);
        return;
      }

      if (isSimulation) {
        dataLoadingRef.current = false;
        setDataLoading(false);
        return;
      }

      const accountId = getAccountId();
      const isCompanyScoped =
        !Object.keys(nonCompanyCollectionNames).includes(collection) &&
        collection !== "transactions";
      if (isCompanyScoped && !accountId) {
        dataLoadingRef.current = false;
        setDataLoading(false);
        return;
      }

      // About to (re)subscribe for real -- reset loading to true first. This
      // matters most for a call site that toggles `collection` between
      // falsy (disabled) and a real name once its own gating data loads
      // (e.g. a rollups-v2 read-cutover flag arriving after useAccountData's
      // own initial empty-object default): without this, dataLoading was
      // already left `false` by the disabled branch above, so the consumer
      // would see an empty result reported as "not loading" until the first
      // real snapshot arrives, rather than correctly loading. Also correct,
      // as a side effect, for the existing collection-toggle hooks
      // (useTransactionsMonthsData, the vendor listener) resubscribing to a
      // genuinely different collection/query -- a no-op re-render when
      // dataLoading is already true.
      dataLoadingRef.current = true;
      setDataLoading(true);

      const firebaseQuery = buildCollectionQuery(
        collection,
        queriesRef.current
      );
      const unsubscribe = onSnapshot(
        firebaseQuery,
        (snap) => {
          const data = snap.docs.map((doc) => ({
            ...unpackFirebaseObj(doc.data()),
            id: doc.id
          }));

          if (dataLoadingRef.current || !isEqual(data, dbDataRef.current)) {
            dbDataRef.current = data;
            dataLoadingRef.current = false;
            setDbData(data);
            setDataLoading(false);
          }
        },
        () => {
          dataLoadingRef.current = false;
          setDataLoading(false);
        }
      );

      return () => unsubscribe();
    };

    // Must return syncData()'s result so React runs the onSnapshot cleanup on
    // unmount/dep-change. The early-return branches yield undefined (no listener
    // created), which React tolerates; the listener path returns the unsubscribe.
    return syncData();
  }, [collection, queriesKey, isSimulation, requestedSubscriptionKey]);

  if (!collection) {
    return [[], false];
  }

  if (viewMode === "admin" && adminDataCollections.includes(collection)) {
    return [
      filterVendorHiddenDocs(collection, adminData[collection]),
      isAdminDataLoading
    ];
  }

  if (isSimulation) {
    const simData = simulationContext[collection] || [];
    return [filterVendorHiddenDocs(collection, simData), false];
  }

  // True on the render(s) between collection/queries changing and this
  // effect actually running for that new key -- see
  // resolvedSubscriptionKeyRef's own comment above for why that render is
  // otherwise observable with stale data and dataLoading===false.
  const isSubscriptionStale =
    resolvedSubscriptionKeyRef.current !== requestedSubscriptionKey;

  return [
    filterVendorHiddenDocs(collection, dbData),
    dataLoading || isSubscriptionStale
  ];
};

// These collections are archived via `archived: true` but predate that field,
// so they can't use buildCollectionQuery's `archived == false` filter — a
// Firestore equality check excludes docs missing the field entirely, which
// would hide every doc created before the field was introduced.
const clientSideArchiveFilteredCollections = ["emissions", "offices"];

export const filterArchivedDocs = (collection, docs) => {
  if (!clientSideArchiveFilteredCollections.includes(collection)) {
    return docs;
  }

  return (docs || []).filter(({ archived }) => !archived);
};

// hiddenByVendor is vendor-relationships' vendor-owned counterpart to the
// buyer-owned `archived` -- it exists only on docs a vendor has hidden from
// their own "customers" alias view, so (like the collections above) it can't
// be a Firestore `== false` query filter without hiding every relationship
// that predates the field. Filtered centrally here so every "customers"
// reader gets it, not just the ones that remembered to check for it locally.
export const filterVendorHiddenDocs = (collection, docs) => {
  if (collection !== "customers") {
    return docs;
  }

  return (docs || []).filter(({ hiddenByVendor }) => !hiddenByVendor);
};

// achOffsetPaymentFailed and refundCustomer (functions/utils/webhooks/stripe.js)
// mark a declined or refunded purchase `status: "failed"` but keep the doc and
// its tonsCo2e, so every offsets reader would otherwise count it toward badges
// and totals. Other statuses stay: "open" is an ACH invoice whose cNaught
// credits were already ordered, and "completed" is a manually recorded
// external purchase.
export const filterFailedOffsets = (collection, docs) => {
  if (collection !== "offsets") {
    return docs;
  }

  return (docs || []).filter(({ status }) => status !== "failed");
};

// A locked accounting year always has `date` and its month-by-subcategory
// breakdown. A doc missing either (e.g. a stub a backend write recreated)
// crashes processAccountingData's flatMap and takes down the whole
// dashboard, and a missing date reads as the current year being locked.
export const filterMalformedAccountingYears = (collection, docs) => {
  if (collection !== "accounting") {
    return docs;
  }

  return (docs || []).filter(
    ({ date, monthlySubcategoryEmissionsTons }) =>
      !!date && Array.isArray(monthlySubcategoryEmissionsTons)
  );
};

const filterDisplayDocs = (collection, docs) =>
  filterMalformedAccountingYears(
    collection,
    filterFailedOffsets(
      collection,
      filterVendorHiddenDocs(collection, filterArchivedDocs(collection, docs))
    )
  );

export const useCachedDisplayData = (collection, queries = []) => {
  const { viewMode, isSimulation } = usePlatformLayoutContext() || {};
  const { adminData = {}, isAdminDataLoading } =
    useContext(AdminDataContext) || {};
  const simulationContext = useSimulationContext() || {};

  // Cache key must include the queries, not just the collection name -- otherwise
  // two components calling this hook for the same collection with different
  // filters (e.g. "transactions" filtered to shipping vs. to utilities) collide
  // on one SWR cache entry and silently read each other's data. Registering
  // the key lets useMutateCachedCollection invalidate this filtered slot too,
  // not just the unfiltered one, when the collection is written elsewhere.
  const queriesKey = serializeQueriesKey(queries);

  // Registered during render, not in an effect: SWR subscribes to the key in a
  // layout effect, so an effect here would leave a commit where the slot is
  // cached but invisible to writers. Adding to a Set is idempotent, so repeated
  // and double-invoked renders are harmless.
  registerCachedCollectionQueryKey(collection, queriesKey);

  const [dbCollection, dbCollectionLoading] = useDestructuredSWRdata(
    [collection, queriesKey],
    () => fetchCollectionRefQuery(collection, queries)
  );

  if (isSimulation) {
    const simData = simulationContext[collection] || [];
    return [filterDisplayDocs(collection, simData), false];
  }

  if (viewMode === "admin" && adminDataCollections.includes(collection)) {
    return [
      filterDisplayDocs(collection, adminData[collection]),
      isAdminDataLoading
    ];
  }

  return [
    filterDisplayDocs(collection, dbCollection),
    dbCollectionLoading
  ];
};

const prepareUpdateCacheObj = (updateObj, existingDoc = {}) =>
  Object.fromEntries(
    Object.entries(updateObj)
      .filter(
        ([, value]) =>
          !(value && typeof value === "object" && value.deletable === true)
      )
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return [
            key,
            value.map((row) =>
              typeof row === "string" || typeof row === "number"
                ? row
                : prepareUpdateCacheObj(row)
            )
          ];
        }

        if (value && typeof value === "object") {
          const { data, arrayUnion, arrayRemove } = value;
          if (!arrayUnion && !arrayRemove) {
            return [key, value];
          }

          const existingField = existingDoc[key];
          if (arrayUnion && Array.isArray(existingField)) {
            return [key, [...existingField, data]];
          }

          if (arrayRemove && Array.isArray(existingField)) {
            const newArray = existingField.filter(({ id }) => id === data.id);
            return [key, newArray];
          }
        }

        return [key, value];
      })
  );

// Collections this generic CRUD layer never writes directly -- firestore.rules
// denies client creates/updates/deletes on these outright, so every mutation
// must go through the guarded backend endpoints below.
const DIRECT_WRITE_ERRORS = {
  transactions:
    "Direct transaction writes are disabled. Use useCreateTransactions()/useUpdateTransactions() from helpers/components/emissions instead.",
  "vendor-relationships":
    "Direct vendor-relationships writes are disabled. Use useUpdateVendorRelationshipFields() from helpers/components/vendors instead.",
  customers:
    "Direct vendor-relationships writes are disabled. Use useUpdateVendorRelationshipFields() from helpers/components/vendors instead."
};

const rejectDirectlyLockedCollectionWrite = (collection) => {
  const errorMessage = DIRECT_WRITE_ERRORS[collection];
  if (!errorMessage) {
    return;
  }

  throw new Error(errorMessage);
};

const addDocRef = async (collection, data, userId) => {
  rejectDirectlyLockedCollectionWrite(collection);

  const collectionRef = buildCollectionRef(collection);

  const preparedFirebaseObj = prepareFirebaseObj({
    ...data,
    _updatedBy: userId || "system"
  });

  return await addDoc(collectionRef, preparedFirebaseObj).then(
    (docRef) => docRef.id
  );
};

const updateDocRef = async ({ collection, docId, updateObj, userId }) => {
  rejectDirectlyLockedCollectionWrite(collection);

  const getDocRef = () => {
    if (collection && docId) {
      return buildDocRef(collection, docId);
    }

    return buildDocRef();
  };

  const docRef = getDocRef();
  const updateFirebaseObj = prepareFirebaseObj({
    ...updateObj,
    _updatedBy: userId || "system"
  });

  return await updateDoc(docRef, updateFirebaseObj);
};

const deleteDocRef = async (collection, docId) => {
  rejectDirectlyLockedCollectionWrite(collection);

  const docRef = buildDocRef(collection, docId);
  return await deleteDoc(docRef);
};

export const useCachedFirebaseCrud = () => {
  const { mutate } = useSWRConfig();
  const mutateCachedCollection = useMutateCachedCollection();
  const { viewMode, isSimulation } = usePlatformLayoutContext() || {};
  const { adminData, editAdminData } = useContext(AdminDataContext) || {};
  const [user] = useAuth();
  const { uid: userId } = user || {};

  const { updateCollection } = useSimulationContext() || {};
  const { canEdit } = useContext(PermissionsContext);

  const viewModeDataObj = {
    admin: {
      viewModeData: adminData,
      editViewModeData: editAdminData
    }
  };
  const { viewModeData, editViewModeData } = viewModeDataObj[viewMode] || {};
  const isViewModeNotCompany = !!viewMode && viewMode !== "company";

  const modifyViewModeData = (dbCollection, modificationFunction) => {
    const currentViewModeData = viewModeData[dbCollection];
    const newViewModeData = modificationFunction(currentViewModeData);

    return editViewModeData(collection, newViewModeData);
  };

  const updateAccountData = async (updateObj) => {
    const updater = (companyData) =>
      prepareUpdateCacheObj(
        {
          ...companyData,
          ...mutateNestedData(companyData, updateObj)
        },
        companyData
      );

    if (!canEdit) {
      return Promise.resolve();
    }

    if (isSimulation) {
      updateCollection("accountData", updater);
      return Promise.resolve();
    }

    // Must match useAccountData's ["accountData", accountId] key -- mutating
    // the old bare "accountData" key here would write to a slot no reader
    // subscribes to, leaving settings/onboarding writes invisible until an
    // unrelated remount happened to revalidate the real slot.
    const accountId = getAccountId();

    mutate(["accountData", accountId], updater, false);

    await updateDocRef({
      updateObj,
      userId
    });

    return mutate(["accountData", accountId]);
  };

  const updateCollectionDoc = async (collection, docId, updateObj) => {
    rejectDirectlyLockedCollectionWrite(collection);

    const updateLocalData = (currentData) => {
      if (!currentData) {
        return;
      }

      return currentData.map((doc) => {
        if (docId === doc.id) {
          const preparedObj = prepareUpdateCacheObj(updateObj, doc);

          const deletableKeys = Object.keys(updateObj).filter(
            (k) => updateObj[k]?.deletable === true
          );
          const baseDoc = deletableKeys.length
            ? Object.fromEntries(
                Object.entries(doc).filter(([k]) => !deletableKeys.includes(k))
              )
            : doc;

          return { ...baseDoc, ...preparedObj };
        }
        return doc;
      });
    };

    if (!canEdit) {
      return Promise.resolve();
    }

    if (isSimulation) {
      updateCollection(collection, updateLocalData);
      return Promise.resolve();
    }

    if (isViewModeNotCompany) {
      return modifyViewModeData(collection, updateLocalData);
    }

    mutateCachedCollection(collection, updateLocalData, false);

    return await updateDocRef({
      collection,
      docId,
      updateObj,
      userId
    })
      .then(() => mutateCachedCollection(collection))
      .catch((e) => {
        console.log("firebase update error: " + JSON.stringify(e));
        mutateCachedCollection(collection);
        throw new Error(e);
      });
  };

  const newCollectionDoc = async (collection, data) => {
    rejectDirectlyLockedCollectionWrite(collection);

    const tempId = generateTempId();

    const addLocalData = (currentCollection = []) => {
      const newObj = isSimulation && data?.id ? data : { ...data, id: tempId };
      return [...currentCollection, newObj];
    };

    if (!canEdit) {
      return Promise.resolve();
    }

    if (isSimulation) {
      updateCollection(collection, addLocalData);
      return Promise.resolve(tempId);
    }

    if (isViewModeNotCompany) {
      return modifyViewModeData(collection, addLocalData);
    }

    mutateCachedCollection(collection, addLocalData, false);

    const docId = await addDocRef(collection, data, userId);

    mutateCachedCollection(collection);
    return docId;
  };

  const deleteCachedCollectionDoc = async (collection, docId) => {
    rejectDirectlyLockedCollectionWrite(collection);

    const deleteLocalData = (currentCollection = []) =>
      currentCollection.filter(({ id }) => id !== docId);

    if (!canEdit) {
      return Promise.resolve();
    }

    if (isViewModeNotCompany) {
      return modifyViewModeData(collection, deleteLocalData);
    }

    mutateCachedCollection(
      collection,
      (currentCollection) =>
        currentCollection && currentCollection.filter(({ id }) => id !== docId),
      false
    );

    return await deleteDocRef(collection, docId).then(() => {
      mutateCachedCollection(collection);
    });
  };

  return {
    updateCollectionDoc,
    updateAccountData,
    newCollectionDoc,
    deleteCachedCollectionDoc
  };
};

const checkStorageFileExists = async (storageRef) => {
  try {
    await getDownloadURL(storageRef);
    return true;
  } catch (e) {
    return false;
  }
};

export const uploadFileToStorage = async ({
  path,
  fileObject,
  fileMetadata,
  hasUserConfirmedUpload
}) => {
  const storageRef = ref(storage, path);

  if (!hasUserConfirmedUpload) {
    const isDuplicateFile = await checkStorageFileExists(storageRef);
    if (isDuplicateFile) {
      return { isDuplicateFileDetected: true };
    }
  }

  const uploadTask = uploadBytesResumable(storageRef, fileObject, fileMetadata);

  return new Promise((resolve, reject) => {
    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );

        console.log("progress", progress);
      },
      (error) => {
        reject(error);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref)
          .then((url) => {
            resolve(url);
          })
          .catch(reject);
      }
    );
  });
};

export const getFileMetadata = async (storagePath, bucketName = null) => {
  try {
    // Use custom bucket if provided, otherwise use default storage
    const storageInstance = bucketName
      ? getStorage(app, `gs://${bucketName}`)
      : storage;

    const storageRef = ref(storageInstance, storagePath);
    const metadata = await getMetadata(storageRef);

    return metadata;
  } catch (error) {
    console.error("Error getting file metadata:", error);
    throw error;
  }
};

export const deleteFileFromStorage = async (path, bucketName = null) => {
  try {
    // Use custom bucket if provided, otherwise use default storage
    const storageInstance = bucketName
      ? getStorage(app, `gs://${bucketName}`)
      : storage;

    const storageRef = ref(storageInstance, path);

    await deleteObject(storageRef);

    return { success: true };
  } catch (error) {
    console.error("Error deleting file from storage:", error);
    throw error;
  }
};

export const getTaskFileDownloadUrl = async (fileBucket, filePath) => {
  const storage = getStorage(app, `gs://${fileBucket}`);
  const fileRef = ref(storage, filePath);

  return await getDownloadURL(fileRef);
};
